import {NodeSearch} from '../../components/NodeSearch'
import {
    ReactFlow,
    Panel,
    Background,
    Controls,
    applyNodeChanges,
    applyEdgeChanges,
    addEdge,
    getNodesBounds,
    getViewportForBounds,
    Node,
    Position,
    MarkerType,
} from '@xyflow/react'
import '@xyflow/react/dist/style.css'
import './flow.css'
import {
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react'
import {Edge} from '@xyflow/react'
import WidgetsIcon from '@mui/icons-material/Widgets'
import SmartToySharpIcon from '@mui/icons-material/SmartToySharp'
import TextFieldsSharpIcon from '@mui/icons-material/TextFieldsSharp'
import ListSharpIcon from '@mui/icons-material/ListSharp'
import ChecklistRtlSharpIcon from '@mui/icons-material/ChecklistRtlSharp'
import TableChartSharpIcon from '@mui/icons-material/TableChartSharp'
import AutoAwesomeSharpIcon from '@mui/icons-material/AutoAwesomeSharp'
import Alert from '@mui/material/Alert'
import Snackbar from '@mui/material/Snackbar'
import {HistoryContext} from '../../contexts/history'
import {Dependency, QuestionFlow, QuestionNode} from '../../types/flowData'
import {v4 as uuidv4} from 'uuid'
import {toPng} from 'html-to-image'
import {FlowApi} from '../../services/FlowApi.service'
import {useNavigate} from 'react-router-dom'
const TREE_HORIZONTAL_SPACING = 260
const TREE_VERTICAL_SPACING = 180

// Interfaces atualizadas para corresponder à nova estrutura
interface QuestionOption {
    title: string
    content: string
    index: number
}

interface TableHeader {
    label: string
    name: string
    size: number
}

interface QuestionType {
    text?: {
        placeholder: string | null
        default: string | null
        allowed_answer_type: string
    }
    select?: {
        options: QuestionOption[]
        default: number
        placeholder: string
        allowed_answer_type: string
    }
    multiselect?: {
        options: QuestionOption[]
        default: number
        placeholder: string
        allowed_answer_type: string
    }
    table?: {
        headers: TableHeader[]
        items: any[]
        type: string
    }
    autonomous?: boolean
}

interface FlowQuestion {
    id: string
    created_at: string
    is_active: boolean
    content: string
    label: string
    instruction: string | null
    assistant_id: string | null
    head: boolean
    question_type:
        | 'text'
        | 'select'
        | 'multiselect'
        | 'table'
        | 'autonomous'
        | 'radio'
        | 'checkbox'
        | 'table_autonomous'
    document_section: string
    type: QuestionType
    following_questions: string[]
    following_question_keys?: string[]
    depends_on: string[]
}

interface FlowData {
    [key: string]: FlowQuestion
}

const normalizeFollowingQuestions = (value: unknown): string[] => {
    if (Array.isArray(value)) {
        return value.filter((item): item is string => typeof item === 'string')
    }

    if (value && typeof value === 'object') {
        return Object.values(value)
            .map((item) => {
                if (typeof item === 'string') return item
                if (item && typeof item === 'object') {
                    const candidate = (item as any).question_id
                    return typeof candidate === 'string' ? candidate : null
                }
                return null
            })
            .filter((item): item is string => Boolean(item))
    }

    return []
}

const normalizeFollowingQuestionKeys = (value: unknown): string[] => {
    if (Array.isArray(value)) {
        return value.filter((item): item is string => typeof item === 'string')
    }

    if (!value || typeof value !== 'object') {
        return []
    }

    return Object.entries(value).reduce((acc, [key, item]) => {
        if (typeof item === 'string') {
            acc.push(key)
            return acc
        }

        if (item && typeof item === 'object') {
            const questionId = (item as any).question_id
            if (typeof questionId === 'string') {
                acc.push(key)
            }
        }

        return acc
    }, [] as string[])
}

const normalizeFlowDataFromContextQuestions = (questions: any[]): FlowData => {
    const normalized: FlowData = {}

    ;(questions || []).forEach((questionNode) => {
        const questionData = questionNode?.question_data || questionNode
        if (!questionData?.id) return

        const followingFromNode = normalizeFollowingQuestions(
            questionNode?.following_questions
        )
        const followingFromQuestionData = normalizeFollowingQuestions(
            questionData?.following_questions
        )
        const followingQuestionKeysFromNode = normalizeFollowingQuestionKeys(
            questionNode?.following_questions
        )
        const followingQuestionKeysFromQuestionData =
            normalizeFollowingQuestionKeys(questionData?.following_questions)
        const followingQuestions =
            followingFromNode.length > 0
                ? followingFromNode
                : followingFromQuestionData
        const followingQuestionKeys =
            followingQuestionKeysFromNode.length > 0
                ? followingQuestionKeysFromNode
                : followingQuestionKeysFromQuestionData

        const dependsOnFromQuestionData = Array.isArray(questionData.depends_on)
            ? questionData.depends_on
                  .map((dependency: any) =>
                      typeof dependency === 'string'
                          ? dependency
                          : dependency?.question_id
                  )
                  .filter(Boolean)
            : []

        const dependsOnFromQuestionNode = Array.isArray(
            questionNode?.depends_on
        )
            ? questionNode.depends_on
                  .map((dependency: any) =>
                      typeof dependency === 'string'
                          ? dependency
                          : dependency?.question_id
                  )
                  .filter(Boolean)
            : []

        const dependsOn = Array.from(
            new Set([
                ...dependsOnFromQuestionNode,
                ...dependsOnFromQuestionData,
            ])
        )

        normalized[questionData.id] = {
            ...questionData,
            head: Boolean(questionNode?.head ?? questionData.head),
            following_questions: followingQuestions,
            following_question_keys: followingQuestionKeys,
            depends_on: dependsOn,
        }
    })

    return normalized
}

const normalizeFlowData = (data: FlowData): FlowData => {
    const normalized: FlowData = {}

    Object.entries(data || {}).forEach(([key, question]) => {
        if (!question) return
        const mapKey = question.id || key
        normalized[mapKey] = question
    })

    return normalized
}

const calculateTreePositions = (
    data: FlowData
): Record<string, {x: number; y: number}> => {
    const normalizedData = normalizeFlowData(data)
    const nodeIds = Object.keys(normalizedData)

    if (nodeIds.length === 0) {
        return {}
    }

    const headNodes = nodeIds.filter((id) => normalizedData[id].head)
    const noDependencyNodes = nodeIds.filter(
        (id) => (normalizedData[id].depends_on || []).length === 0
    )

    const rootCandidates = headNodes.length
        ? headNodes
        : noDependencyNodes.length
          ? noDependencyNodes
          : [nodeIds[0]]

    const depthMap: Record<string, number> = {}
    const queue: Array<{id: string; depth: number}> = []

    rootCandidates.forEach((rootId) => {
        if (!normalizedData[rootId]) return
        depthMap[rootId] = 0
        queue.push({id: rootId, depth: 0})
    })

    while (queue.length > 0) {
        const current = queue.shift()!
        const currentNode = normalizedData[current.id]
        if (!currentNode) continue

        const children = currentNode.following_questions || []
        children.forEach((childId) => {
            if (!normalizedData[childId]) return
            const nextDepth = current.depth + 1
            if (
                depthMap[childId] === undefined ||
                nextDepth < depthMap[childId]
            ) {
                depthMap[childId] = nextDepth
                queue.push({id: childId, depth: nextDepth})
            }
        })
    }

    const assignedDepths = Object.values(depthMap)
    const maxAssignedDepth = assignedDepths.length
        ? Math.max(...assignedDepths)
        : 0

    nodeIds.forEach((id) => {
        if (depthMap[id] === undefined) {
            depthMap[id] = maxAssignedDepth + 1
        }
    })

    const levels = new Map<number, string[]>()
    Object.entries(depthMap).forEach(([id, depth]) => {
        if (!levels.has(depth)) {
            levels.set(depth, [])
        }
        levels.get(depth)!.push(id)
    })

    levels.forEach((levelNodes, _depth) => {
        levelNodes.sort((a, b) => {
            const labelA = normalizedData[a]?.label || ''
            const labelB = normalizedData[b]?.label || ''
            return labelA.localeCompare(labelB)
        })
    })

    const positions: Record<string, {x: number; y: number}> = {}
    let minY = Infinity

    Array.from(levels.entries())
        .sort(([depthA], [depthB]) => depthA - depthB)
        .forEach(([depth, levelNodes]) => {
            const offset = ((levelNodes.length - 1) * TREE_VERTICAL_SPACING) / 2
            levelNodes.forEach((nodeId, index) => {
                const x = depth * TREE_HORIZONTAL_SPACING
                const y = index * TREE_VERTICAL_SPACING - offset
                positions[nodeId] = {x, y}
                if (y < minY) {
                    minY = y
                }
            })
        })

    if (minY !== Infinity && minY < 0) {
        const shift = Math.abs(minY) + TREE_VERTICAL_SPACING
        Object.keys(positions).forEach((nodeId) => {
            positions[nodeId].y += shift
        })
    }

    return positions
}

// Interface local para os nós do ReactFlow
interface Question {
    id: string
    titulo: string
    prompt: string
    tipo:
        | 'text'
        | 'select'
        | 'multiSelect'
        | 'table'
        | 'radio'
        | 'checkbox'
        | 'autonomous'
        | 'table_autonomous'
    conteudo: string | Record<string, string>
    followed: string[]
    depends_on?: string[]
    following_question_keys?: string[]
    created_at?: string
    instruction?: string | null
    originalData?: FlowQuestion // Manter referência aos dados originais
}

const isOptionBasedQuestionType = (questionType: Question['tipo']) => {
    return ['select', 'multiSelect', 'radio', 'checkbox'].includes(questionType)
}

const getDefaultFollowingQuestionKey = (index: number) => {
    return index === 0 ? 'Default' : `Default ${index + 1}`
}

const initialNodes: Node[] = []
const initialEdges: Edge[] = []

const getQuestionIcon = (questionType: Question['tipo']) => {
    switch (questionType) {
        case 'autonomous':
            return <SmartToySharpIcon fontSize="small" />
        case 'text':
            return <TextFieldsSharpIcon fontSize="small" />
        case 'select':
            return <ListSharpIcon fontSize="small" />
        case 'multiSelect':
        case 'checkbox':
            return <ChecklistRtlSharpIcon fontSize="small" />
        case 'table':
            return <TableChartSharpIcon fontSize="small" />
        case 'table_autonomous':
            return <AutoAwesomeSharpIcon fontSize="small" />
        default:
            return <WidgetsIcon fontSize="small" />
    }
}

const buildNodeLabel = (text: string, questionType: Question['tipo']) => (
    <div
        style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
        }}
    >
        <span
            style={{
                width: '24px',
                height: '24px',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                border: '1px solid #d1d5db',
                borderRadius: '6px',
                backgroundColor: '#f8fafc',
                flexShrink: 0,
            }}
        >
            {getQuestionIcon(questionType)}
        </span>
        <span>{text}</span>
    </div>
)

export default function FlowCreator() {
    const navigate = useNavigate()
    const {
        flowQuestions: contextFlowData,
        flows,
        setFlowData,
        setFlows,
        getFlow,
    } = useContext(HistoryContext)
    const flowWrapperRef = useRef<HTMLDivElement | null>(null)
    const flowApi = useMemo(() => new FlowApi(), [])
    const [nodes, setNodes] = useState(initialNodes)
    const [edges, setEdges] = useState(initialEdges)
    const [showModal, setShowModal] = useState(false)
    const [showModalFlow, setShowModalFlow] = useState(false)
    const [showCreateFlowModal, setShowCreateFlowModal] = useState(false)
    const [showCloneFlowModal, setShowCloneFlowModal] = useState(false)
    const [selectedNodeForModal, setSelectedNodeForModal] =
        useState<Node | null>(null)
    const [isLoading, setIsLoading] = useState(true)
    const [isCreatingFlow, setIsCreatingFlow] = useState(false)

    const [nodeTitle, setNodeTitle] = useState('')
    const [questionType, setQuestionType] = useState<
        | 'text'
        | 'select'
        | 'multiSelect'
        | 'table'
        | 'radio'
        | 'checkbox'
        | 'autonomous'
        | 'table_autonomous'
    >('text')
    const [questionContent, setQuestionContent] = useState<string>('')
    const [autonomousInstruction, setAutonomousInstruction] = useState('')
    const [optionsDict, setOptionsDict] = useState<Record<string, string>>({})
    const [draggedOptionKey, setDraggedOptionKey] = useState<string | null>(
        null
    )
    const [dragOverOptionKey, setDragOverOptionKey] = useState<string | null>(
        null
    )
    const [followedNodes, setFollowedNodes] = useState<string[]>([])
    const [dependsOnNodes, setDependsOnNodes] = useState<string[]>([])
    const [followedNodeKeys, setFollowedNodeKeys] = useState<string[]>([])
    const [flowFilter, setFlowFilter] = useState('')
    const [newFlowName, setNewFlowName] = useState('')
    const [cloneFlowName, setCloneFlowName] = useState('')
    const [cloneFlowFilter, setCloneFlowFilter] = useState('')
    const [selectedFlowToCloneId, setSelectedFlowToCloneId] = useState('')
    const [snackbarOpen, setSnackbarOpen] = useState(false)
    const [snackbarMessage, setSnackbarMessage] = useState('')
    const [snackbarSeverity, setSnackbarSeverity] = useState<
        'success' | 'error'
    >('success')

    const showFeedback = useCallback(
        (message: string, severity: 'success' | 'error') => {
            setSnackbarMessage(message)
            setSnackbarSeverity(severity)
            setSnackbarOpen(true)
        },
        []
    )

    const handleCloseSnackbar = useCallback(
        (_event?: Event | React.SyntheticEvent, reason?: string) => {
            if (reason === 'clickaway') {
                return
            }
            setSnackbarOpen(false)
        },
        []
    )

    const flowQuestions = useMemo<QuestionFlow | null>(() => {
        if (!contextFlowData) {
            return null
        }

        const incomingByNodeId: Record<string, number> = {}
        edges.forEach((edge) => {
            incomingByNodeId[edge.target] =
                (incomingByNodeId[edge.target] || 0) + 1
        })

        const originalByQuestionId = new Map<string, QuestionNode>()
        ;(contextFlowData.questions || []).forEach((questionNode) => {
            const questionId =
                questionNode?.question_data?.id || questionNode?.id
            if (questionId) {
                originalByQuestionId.set(questionId, questionNode)
            }
        })

        const convertedQuestions: QuestionNode[] = nodes.map((node) => {
            const localQuestion = node.data.question as Question
            const originalQuestionNode = originalByQuestionId.get(node.id)
            const originalData =
                originalQuestionNode?.question_data ||
                localQuestion?.originalData
            const originalDataAny = originalData as any

            const normalizedDependsOn: Dependency[] = (
                localQuestion?.depends_on ||
                originalQuestionNode?.depends_on ||
                originalDataAny?.depends_on ||
                []
            ).map((dependency: any) => {
                if (typeof dependency === 'string') {
                    return {question_id: dependency}
                }

                return dependency
            })

            const followedFromEdges = edges
                .filter((edge) => edge.source === node.id)
                .map((edge) => edge.target)

            const followedIds =
                (localQuestion?.followed || []).length > 0
                    ? localQuestion.followed
                    : followedFromEdges

            const normalizedFollowed = followedIds.filter((targetId) =>
                nodes.some((candidate) => candidate.id === targetId)
            )

            const optionBasedType = isOptionBasedQuestionType(
                localQuestion?.tipo
            )

            const optionContentKeys =
                optionBasedType &&
                localQuestion?.conteudo &&
                typeof localQuestion.conteudo === 'object'
                    ? Object.values(localQuestion.conteudo)
                          .map((value) => String(value).trim())
                          .filter((value) => value !== '')
                    : []

            const customFollowingKeys =
                localQuestion?.following_question_keys || []

            const followingQuestionsRecord = normalizedFollowed.reduce(
                (acc, targetId, index) => {
                    const customKey = (customFollowingKeys[index] || '').trim()
                    const key =
                        customKey ||
                        (optionBasedType && optionContentKeys[index]
                            ? optionContentKeys[index]
                            : index === 0
                              ? 'Default'
                              : `Default ${index + 1}`)

                    acc[key] = targetId
                    return acc
                },
                {} as Record<string, string>
            )

            const convertedData = convertLocalQuestionToFlowData(localQuestion)

            const mappedQuestionType =
                convertedData.question_type ||
                (localQuestion.tipo === 'multiSelect'
                    ? 'multiselect'
                    : localQuestion.tipo)

            return {
                id: originalQuestionNode?.id || node.id,
                head:
                    incomingByNodeId[node.id] === undefined ||
                    incomingByNodeId[node.id] === 0,
                following_questions: followingQuestionsRecord,
                depends_on: normalizedDependsOn,
                question_data: {
                    id: node.id,
                    created_at:
                        originalDataAny?.created_at ||
                        localQuestion?.created_at ||
                        new Date().toISOString(),
                    is_active: originalDataAny?.is_active ?? true,
                    name: originalDataAny?.name ?? null,
                    content:
                        localQuestion.prompt ||
                        (convertedData.content as string) ||
                        originalDataAny?.content ||
                        '',
                    label: convertedData.label || localQuestion.titulo,
                    instruction:
                        convertedData.instruction ??
                        originalDataAny?.instruction ??
                        null,
                    assistant_id: originalDataAny?.assistant_id ?? null,
                    question_type: mappedQuestionType,
                    type: convertedData.type ||
                        originalDataAny?.type || {
                            text: {
                                placeholder: null,
                                default: null,
                                allowed_answer_type: 'text',
                            },
                        },
                    depends_on: normalizedDependsOn,
                    document_section: originalDataAny?.document_section ?? null,
                    following_questions: normalizedFollowed,
                    head:
                        incomingByNodeId[node.id] === undefined ||
                        incomingByNodeId[node.id] === 0,
                },
            }
        })

        return {
            ...contextFlowData,
            questions: convertedQuestions,
        }
    }, [contextFlowData, nodes, edges])

    const handleUpdateFlow = async () => {
        if (!flowQuestions) {
            return
        }

        const {errors: _errors, ...flowQuestionsWithoutErrors} = flowQuestions

        const normalizeDependsOnToIds = (dependsOn: any): string[] => {
            if (!Array.isArray(dependsOn)) {
                return []
            }

            return dependsOn
                .map((dependency: any) => {
                    if (typeof dependency === 'string') {
                        return dependency
                    }

                    if (dependency && typeof dependency === 'object') {
                        return dependency.question_id
                    }

                    return null
                })
                .filter((dependencyId): dependencyId is string =>
                    Boolean(dependencyId)
                )
        }

        const questionsWithDependsOnIds = (
            flowQuestionsWithoutErrors.questions || []
        ).map((question: any) => {
            const normalizedDependsOn = normalizeDependsOnToIds(
                question?.depends_on
            )

            return {
                ...question,
                depends_on: normalizedDependsOn,
                question_data: {
                    ...question?.question_data,
                    depends_on: normalizeDependsOnToIds(
                        question?.question_data?.depends_on
                    ),
                },
            }
        })

        try {
            await flowApi.updateFlow({
                document_flow: {
                    ...flowQuestionsWithoutErrors,
                    questions: questionsWithDependsOnIds,
                },
            })
            showFeedback('Fluxo salvo com sucesso.', 'success')
        } catch (error) {
            console.error('Erro ao salvar fluxo:', error)
            showFeedback('Falha ao salvar o fluxo.', 'error')
        }
    }

    const handleDownloadFlowImage = useCallback(async () => {
        try {
            if (nodes.length === 0) {
                showFeedback(
                    'Não há perguntas no fluxo para exportar.',
                    'error'
                )
                return
            }

            const viewportElement = flowWrapperRef.current?.querySelector(
                '.react-flow__viewport'
            ) as HTMLElement | null

            if (!viewportElement) {
                showFeedback(
                    'Não foi possível localizar o fluxo para baixar a imagem.',
                    'error'
                )
                return
            }

            const nodesBounds = getNodesBounds(nodes)
            const exportPadding = 80
            const exportBounds = {
                x: nodesBounds.x - exportPadding,
                y: nodesBounds.y - exportPadding,
                width: nodesBounds.width + exportPadding * 2,
                height: nodesBounds.height + exportPadding * 2,
            }

            const exportWidth = Math.max(1, Math.ceil(exportBounds.width))
            const exportHeight = Math.max(1, Math.ceil(exportBounds.height))

            const viewport = getViewportForBounds(
                exportBounds,
                exportWidth,
                exportHeight,
                0.1,
                1,
                0
            )

            const dataUrl = await toPng(viewportElement, {
                backgroundColor: '#ffffff',
                cacheBust: true,
                pixelRatio: 2,
                width: exportWidth,
                height: exportHeight,
                style: {
                    width: `${exportWidth}px`,
                    height: `${exportHeight}px`,
                    transform: `translate(${viewport.x}px, ${viewport.y}px) scale(${viewport.zoom})`,
                    transformOrigin: 'top left',
                },
            })

            const baseName = (contextFlowData?.name || 'fluxo')
                .trim()
                .toLowerCase()
                .replace(/\s+/g, '-')
                .replace(/[^a-z0-9-_]/g, '')
            const fileName = `${baseName || 'fluxo'}-${new Date()
                .toISOString()
                .slice(0, 10)}.png`

            const link = document.createElement('a')
            link.download = fileName
            link.href = dataUrl
            link.click()

            showFeedback('Imagem do fluxo baixada com sucesso.', 'success')
        } catch (error) {
            console.error('Erro ao baixar imagem do fluxo:', error)
            showFeedback('Falha ao baixar imagem do fluxo.', 'error')
        }
    }, [contextFlowData?.name, nodes, showFeedback])

    // Função para converter os dados do flow em nós do ReactFlow
    const convertFlowDataToNodes = useCallback(
        (data: FlowData): {nodes: Node[]; edges: Edge[]} => {
            const normalizedData = normalizeFlowData(data)
            const newNodes: Node[] = []
            const newEdges: Edge[] = []
            const nodePositions = calculateTreePositions(normalizedData)

            Object.values(normalizedData).forEach((questionData) => {
                // Converter tipo de pergunta
                let localQuestionType: Question['tipo'] = 'text'
                let content: string | Record<string, string> =
                    questionData.content
                const promptContent = questionData.content || ''

                switch (questionData.question_type) {
                    case 'text':
                        localQuestionType = 'text'
                        break
                    case 'select':
                        localQuestionType = 'select'
                        if (questionData.type.select?.options) {
                            const optionsDict: Record<string, string> = {}
                            questionData.type.select.options.forEach(
                                (option) => {
                                    optionsDict[option.title] = option.content
                                }
                            )
                            content = optionsDict
                        }
                        break
                    case 'multiselect':
                        localQuestionType = 'multiSelect'
                        if (questionData.type.multiselect?.options) {
                            const optionsDict: Record<string, string> = {}
                            questionData.type.multiselect.options.forEach(
                                (option) => {
                                    optionsDict[option.title] = option.content
                                }
                            )
                            content = optionsDict
                        }
                        break
                    case 'table':
                        localQuestionType = 'table'
                        if (questionData.type.table?.headers) {
                            const headersDict: Record<string, string> = {}
                            questionData.type.table.headers.forEach(
                                (header) => {
                                    headersDict[header.label] = header.name
                                }
                            )
                            content = headersDict
                        } else {
                            content = questionData.content
                        }
                        break
                    case 'autonomous':
                        localQuestionType = 'autonomous'
                        break
                    default:
                        localQuestionType = 'text'
                }

                const question: Question = {
                    id: questionData.id,
                    titulo: questionData.label,
                    prompt: promptContent,
                    tipo: localQuestionType,
                    conteudo: content,
                    followed: questionData.following_questions || [],
                    following_question_keys:
                        questionData.following_question_keys || [],
                    created_at: questionData.created_at,
                    instruction: questionData.instruction ?? '',
                    originalData: questionData,
                }

                const node: Node = {
                    id: questionData.id,
                    position: nodePositions[questionData.id] || {
                        x: Math.random() * 500,
                        y: Math.random() * 500,
                    },
                    data: {
                        label: buildNodeLabel(
                            questionData.label,
                            localQuestionType
                        ),
                        rawLabel: questionData.label,
                        question: question,
                    },
                    type: questionData.head ? 'input' : 'default',
                    style: getNodeStyle(localQuestionType),
                    sourcePosition: Position.Right,
                    targetPosition: Position.Left,
                }

                newNodes.push(node)

                // Criar edges baseadas nas following_questions
                const createdEdgeIds = new Set<string>()
                questionData.following_questions?.forEach((targetId) => {
                    if (!normalizedData[targetId]) return
                    const edgeId = `${questionData.id}-${targetId}`
                    if (createdEdgeIds.has(edgeId)) return

                    createdEdgeIds.add(edgeId)
                    const edge: Edge = {
                        id: edgeId,
                        source: questionData.id,
                        target: targetId,
                        type: 'default',
                    }
                    newEdges.push(edge)
                })
            })

            return {nodes: newNodes, edges: newEdges}
        },
        []
    )

    // Carregar dados do flow
    useEffect(() => {
        getFlow()
    }, [])

    useEffect(() => {
        if (contextFlowData?.questions?.length) {
            const normalizedContextFlow = normalizeFlowDataFromContextQuestions(
                contextFlowData.questions
            )
            const {nodes: contextNodes, edges: contextEdges} =
                convertFlowDataToNodes(normalizedContextFlow)

            setNodes(contextNodes)
            setEdges(contextEdges)
            setIsLoading(false)
            return
        }

        setNodes([])
        setEdges([])
        setIsLoading(false)
    }, [contextFlowData, convertFlowDataToNodes])

    useEffect(() => {
        const expectedEdges: Edge[] = []
        const expectedEdgeIdSet = new Set<string>()

        nodes.forEach((node) => {
            const question = node.data.question as Question
            const followedIds = question?.followed || []

            followedIds.forEach((targetId) => {
                const targetExists = nodes.some((n) => n.id === targetId)

                if (targetExists) {
                    const edgeId = `${node.id}-${targetId}`
                    if (expectedEdgeIdSet.has(edgeId)) {
                        return
                    }

                    expectedEdgeIdSet.add(edgeId)
                    expectedEdges.push({
                        id: edgeId,
                        source: node.id,
                        target: targetId,
                        type: 'default',
                    })
                }
            })
        })

        const currentEdgeIds = edges.map((edge) => edge.id).sort()
        const expectedEdgeIdsList = expectedEdges.map((edge) => edge.id).sort()

        const edgesChanged =
            JSON.stringify(currentEdgeIds) !==
            JSON.stringify(expectedEdgeIdsList)

        if (edgesChanged) {
            setEdges(expectedEdges)
        }
    }, [nodes])

    useEffect(() => {
        let needsUpdate = false

        const updatedNodes = nodes.map((node) => {
            const question = node.data.question as Question
            const currentFollowed = question?.followed || []

            const edgeBasedFollowed = edges
                .filter((edge) => edge.source === node.id)
                .map((edge) => edge.target)

            const edgeBasedSet = new Set(edgeBasedFollowed)
            const preservedOrderFollowed = currentFollowed.filter((targetId) =>
                edgeBasedSet.has(targetId)
            )
            const newTargetsFromEdges = edgeBasedFollowed.filter(
                (targetId) => !preservedOrderFollowed.includes(targetId)
            )
            const nextFollowed = [
                ...preservedOrderFollowed,
                ...newTargetsFromEdges,
            ]

            const followedChanged =
                JSON.stringify(currentFollowed) !== JSON.stringify(nextFollowed)

            if (followedChanged) {
                console.log(`📝 Atualizando followed do nó ${node.id}:`)
                console.log(`  - Antes: [${currentFollowed.join(', ')}]`)
                console.log(`  - Depois: [${nextFollowed.join(', ')}]`)

                needsUpdate = true

                return {
                    ...node,
                    data: {
                        ...node.data,
                        question: {
                            ...question,
                            id: question?.id || node.id,
                            titulo:
                                question?.titulo ||
                                (node.data.rawLabel as string),
                            prompt: question?.prompt || '',
                            tipo: question?.tipo || 'text',
                            conteudo: question?.conteudo || '',
                            followed: nextFollowed,
                            depends_on: question?.depends_on || [],
                        },
                    },
                }
            }

            return node
        })

        if (needsUpdate) {
            setNodes(updatedNodes)
        }
    }, [edges])

    const getNodeStyle = (_questionType: Question['tipo']) => {
        return {
            backgroundColor: '#ffffff',
            border: '1px solid #e0e0e0',
            color: '#111111',
            borderRadius: '10px',
            padding: '12px',
            boxShadow: '0 2px 6px rgba(15, 23, 42, 0.08)',
        }
    }

    // Função para converter questão local de volta para o formato original
    function convertLocalQuestionToFlowData(
        question: Question
    ): Partial<FlowQuestion> {
        const baseData: Partial<FlowQuestion> = {
            content: question.prompt,
            label: question.titulo,
            question_type:
                question.tipo === 'multiSelect' ? 'multiselect' : question.tipo,
            following_questions: question.followed,
            depends_on: question.depends_on || [],
        }

        // Configurar type baseado no tipo da pergunta
        switch (question.tipo) {
            case 'text':
                baseData.type = {
                    text: {
                        placeholder: null,
                        default: null,
                        allowed_answer_type: 'text',
                    },
                }
                break
            case 'select':
            case 'multiSelect':
                if (typeof question.conteudo === 'object') {
                    const options: QuestionOption[] = Object.entries(
                        question.conteudo
                    ).map(([title, content], index) => ({
                        title,
                        content,
                        index,
                    }))

                    const selectType = {
                        options,
                        default: -1,
                        placeholder: `Selecione uma opção...`,
                        allowed_answer_type: 'text',
                    }

                    baseData.type =
                        question.tipo === 'select'
                            ? {select: selectType}
                            : {multiselect: selectType}
                }
                break
            case 'table':
                {
                    const headersFromContent =
                        typeof question.conteudo === 'object'
                            ? Object.entries(question.conteudo)
                                  .map(([label, name]) => ({
                                      label: label.trim(),
                                      name: String(name).trim(),
                                      size: 1,
                                  }))
                                  .filter(
                                      (header) =>
                                          header.label !== '' &&
                                          header.name !== ''
                                  )
                            : []

                    baseData.type = {
                        table: {
                            headers:
                                headersFromContent.length > 0
                                    ? headersFromContent
                                    : [
                                          {
                                              label: 'name',
                                              name: 'Nome do Item',
                                              size: 1,
                                          },
                                          {
                                              label: 'description',
                                              name: 'Descrição',
                                              size: 1,
                                          },
                                          {
                                              label: 'quantity',
                                              name: 'Quantidade',
                                              size: 1,
                                          },
                                          {
                                              label: 'price',
                                              name: 'Preço',
                                              size: 1,
                                          },
                                      ],
                            items: [],
                            type: 'default',
                        },
                    }
                }
                break
            case 'autonomous':
                baseData.type = {autonomous: true}
                baseData.instruction = question.instruction || ''
                break
        }

        return baseData
    }

    const onElementsRemove = useCallback((elementsToRemove: any[]) => {
        setNodes((nds) =>
            nds.filter(
                (node) => !elementsToRemove.some((el) => el.id === node.id)
            )
        )
        setEdges((eds) =>
            eds.filter(
                (edge) => !elementsToRemove.some((el) => el.id === edge.id)
            )
        )
    }, [])

    const onKeyDown = useCallback(
        (event: KeyboardEvent) => {
            if (event.key === 'Delete') {
                const selectedNodes = nodes.filter((node) => node.selected)
                const selectedEdges = edges.filter((edge) => edge.selected)
                const elementsToRemove = [...selectedNodes, ...selectedEdges]

                if (elementsToRemove.length > 0) {
                    onElementsRemove(elementsToRemove)
                }
            }
        },
        [nodes, edges, onElementsRemove]
    )

    useEffect(() => {
        document.addEventListener('keydown', onKeyDown)
        return () => {
            document.removeEventListener('keydown', onKeyDown)
        }
    }, [onKeyDown])

    const onNodesChange = useCallback(
        (changes: any) =>
            setNodes((nodesSnapshot) =>
                applyNodeChanges(changes, nodesSnapshot)
            ),
        []
    )

    const onEdgesChange = useCallback(
        (changes: any) =>
            setEdges((edgesSnapshot) =>
                applyEdgeChanges(changes, edgesSnapshot)
            ),
        []
    )

    const onConnect = useCallback((params: any) => {
        setEdges((edgesSnapshot) => addEdge(params, edgesSnapshot))

        setNodes((nds) =>
            nds.map((node) => {
                if (node.id === params.source) {
                    const currentQuestion = node.data.question as Question
                    const currentFollowed = currentQuestion?.followed || []

                    const newFollowed = currentFollowed.includes(params.target)
                        ? currentFollowed
                        : [...currentFollowed, params.target]

                    return {
                        ...node,
                        data: {
                            ...node.data,
                            question: {
                                ...currentQuestion,
                                followed: newFollowed,
                            },
                        },
                    }
                }
                return node
            })
        )
    }, [])

    const onNodeClick = useCallback(
        (_event: any, node: Node) => {
            setSelectedNodeForModal(node)
            const question = node.data.question as Question

            const normalizedDependsOn = Array.isArray(question?.depends_on)
                ? question.depends_on
                : Array.isArray(question?.originalData?.depends_on)
                  ? (question.originalData?.depends_on || [])
                        .map((dependency) =>
                            typeof dependency === 'string'
                                ? dependency
                                : (dependency as any)?.question_id
                        )
                        .filter((dependencyId): dependencyId is string =>
                            Boolean(dependencyId)
                        )
                  : []

            setNodeTitle(question?.titulo || (node.data.rawLabel as string))
            setQuestionType(question?.tipo || 'text')
            const nodeFollowed = question?.followed || []
            setFollowedNodes([...nodeFollowed])

            setQuestionContent(question?.prompt || '')
            setAutonomousInstruction('')
            setOptionsDict({})
            setDependsOnNodes(Array.from(new Set(normalizedDependsOn)))

            if (question?.tipo === 'text' || question?.tipo === 'autonomous') {
                setQuestionContent(question?.prompt || '')
            } else if (
                question?.tipo === 'select' ||
                question?.tipo === 'multiSelect' ||
                question?.tipo === 'table' ||
                question?.tipo === 'radio' ||
                question?.tipo === 'checkbox'
            ) {
                let existingOptions = question.conteudo as Record<
                    string,
                    string
                >

                if (
                    question?.tipo === 'table' &&
                    (!existingOptions || typeof existingOptions !== 'object')
                ) {
                    const headers =
                        question?.originalData?.type?.table?.headers || []
                    existingOptions = headers.reduce(
                        (acc, header) => {
                            acc[header.label] = header.name
                            return acc
                        },
                        {} as Record<string, string>
                    )
                }

                setOptionsDict(existingOptions || {})
            }

            if (question?.tipo === 'autonomous') {
                setAutonomousInstruction(
                    question?.originalData?.instruction ??
                        question?.instruction ??
                        ''
                )
            }

            const originalQuestionNode = (
                contextFlowData?.questions || []
            ).find(
                (questionNode: any) =>
                    (questionNode?.question_data?.id || questionNode?.id) ===
                    node.id
            )

            const followingFromApi = originalQuestionNode?.following_questions
            const followingKeysFromApi: string[] =
                followingFromApi &&
                typeof followingFromApi === 'object' &&
                !Array.isArray(followingFromApi)
                    ? Object.entries(followingFromApi)
                          .filter(([, value]) => {
                              if (typeof value === 'string') {
                                  return true
                              }

                              return Boolean(
                                  value &&
                                  typeof value === 'object' &&
                                  typeof (value as any).question_id === 'string'
                              )
                          })
                          .map(([key]) => key)
                    : []

            const optionValues =
                question?.conteudo && typeof question.conteudo === 'object'
                    ? Object.values(question.conteudo)
                          .map((value) => String(value).trim())
                          .filter((value) => value !== '')
                    : []

            const savedFollowingKeys = question?.following_question_keys || []
            const initialFollowingKeys: string[] = []

            nodeFollowed.forEach((targetId, index) => {
                const savedKey = (savedFollowingKeys[index] || '').trim()
                const apiKey = (followingKeysFromApi[index] || '').trim()
                const optionKey = optionValues[index] || ''

                initialFollowingKeys[index] =
                    savedKey ||
                    apiKey ||
                    (isOptionBasedQuestionType(question?.tipo || 'text')
                        ? optionKey
                        : getDefaultFollowingQuestionKey(index))
            })

            setFollowedNodeKeys(initialFollowingKeys)

            setShowModal(true)
        },
        [contextFlowData]
    )

    const addNewNode = useCallback(() => {
        const newNodeId = uuidv4()
        const newQuestion: Question = {
            id: newNodeId,
            titulo: `pergunta_${nodes.length + 1}`,
            prompt: `Pergunta ${nodes.length + 1}`,
            tipo: 'text',
            conteudo: '',
            followed: [],
            depends_on: [],
            created_at: new Date().toISOString(),
            instruction: '',
        }

        const newNode = {
            id: newNodeId,
            position: {
                x: Math.random() * 300,
                y: Math.random() * 300,
            },
            data: {
                label: buildNodeLabel(newQuestion.titulo, newQuestion.tipo),
                rawLabel: newQuestion.titulo,
                question: newQuestion,
            },
            type: 'default',
            style: getNodeStyle(newQuestion.tipo),
            sourcePosition: Position.Right,
            targetPosition: Position.Left,
        }

        setNodeTitle(newQuestion.titulo)
        setQuestionType(newQuestion.tipo)
        setQuestionContent(newQuestion.prompt)
        setAutonomousInstruction('')
        setOptionsDict({})
        setFollowedNodes([])
        setDependsOnNodes([])
        setFollowedNodeKeys([])
        setSelectedNodeForModal(newNode as Node)
        setShowModal(true)
    }, [nodes.length])

    const handleSaveQuestion = useCallback(() => {
        if (!selectedNodeForModal) return

        const currentQuestion = selectedNodeForModal.data.question as
            | Question
            | undefined

        let content: string | Record<string, string>

        if (questionType === 'text') {
            content = questionContent
        } else if (
            questionType === 'select' ||
            questionType === 'multiSelect' ||
            questionType === 'table' ||
            questionType === 'radio' ||
            questionType === 'checkbox'
        ) {
            const optionBasedType = isOptionBasedQuestionType(questionType)

            if (optionBasedType) {
                const entries = Object.entries(optionsDict)

                if (entries.length === 0) {
                    showFeedback(
                        'Adicione ao menos uma opção com valor preenchido para salvar a pergunta.',
                        'error'
                    )
                    return
                }

                const hasEmptyLabel = entries.some(
                    ([label]) => label.trim() === ''
                )
                const hasEmptyValue = entries.some(
                    ([, value]) => value.trim() === ''
                )

                if (hasEmptyLabel || hasEmptyValue) {
                    showFeedback(
                        'Preencha todos os campos de opção (texto e valor) para salvar a pergunta.',
                        'error'
                    )
                    return
                }

                const normalizedOptionValues = entries.map(([, value]) =>
                    value.trim()
                )
                const duplicatedOptionValue = normalizedOptionValues.find(
                    (value, index) =>
                        normalizedOptionValues.indexOf(value) !== index
                )

                if (duplicatedOptionValue) {
                    showFeedback(
                        'Os valores enviados das opções devem ser únicos.',
                        'error'
                    )
                    return
                }
            }

            const filteredOptions: Record<string, string> = {}
            Object.entries(optionsDict).forEach(([key, value]) => {
                if (key.trim() !== '' && value.trim() !== '') {
                    filteredOptions[key] = value
                }
            })
            content = filteredOptions
        } else {
            content = questionContent
        }

        const optionValues =
            questionType !== 'text' &&
            questionType !== 'autonomous' &&
            content &&
            typeof content === 'object'
                ? Object.values(content)
                      .map((value) => String(value).trim())
                      .filter((value) => value !== '')
                : []

        const resolvedFollowingKeys: string[] = []
        const usedFollowingKeys = new Set<string>()

        for (let index = 0; index < followedNodes.length; index += 1) {
            const customKey = (followedNodeKeys[index] || '').trim()
            const fallbackKey = isOptionBasedQuestionType(questionType)
                ? optionValues[index] || ''
                : getDefaultFollowingQuestionKey(index)
            const resolvedKey = customKey || fallbackKey

            if (resolvedKey === '') {
                showFeedback(
                    'Preencha a chave de todas as ligações em Próximos Nós para salvar a pergunta.',
                    'error'
                )
                return
            }

            if (usedFollowingKeys.has(resolvedKey)) {
                showFeedback(
                    'As chaves de Próximos Nós devem ser únicas.',
                    'error'
                )
                return
            }

            usedFollowingKeys.add(resolvedKey)
            resolvedFollowingKeys[index] = resolvedKey
        }

        const updatedQuestion: Question = {
            id: selectedNodeForModal.id,
            titulo: nodeTitle,
            prompt: questionContent,
            tipo: questionType,
            conteudo: content,
            followed: followedNodes,
            depends_on: questionType === 'autonomous' ? dependsOnNodes : [],
            following_question_keys: resolvedFollowingKeys,
            created_at: currentQuestion?.created_at || new Date().toISOString(),
            instruction:
                questionType === 'autonomous'
                    ? autonomousInstruction
                    : undefined,
        }

        // Verificar se é um novo nó (não existe na lista ainda)
        const isNewNode = !nodes.some((n) => n.id === selectedNodeForModal.id)

        if (isNewNode) {
            // Adicionar novo nó
            const newNode: Node = {
                ...selectedNodeForModal,
                data: {
                    ...selectedNodeForModal.data,
                    label: buildNodeLabel(nodeTitle, questionType),
                    rawLabel: nodeTitle,
                    question: updatedQuestion,
                },
                style: getNodeStyle(questionType),
            }
            setNodes((nds) => [...nds, newNode])
        } else {
            // Atualizar nó existente
            setNodes((nds) =>
                nds.map((node) => {
                    if (node.id === selectedNodeForModal.id) {
                        return {
                            ...node,
                            data: {
                                ...node.data,
                                label: buildNodeLabel(nodeTitle, questionType),
                                rawLabel: nodeTitle,
                                question: updatedQuestion,
                            },
                            style: getNodeStyle(questionType),
                        }
                    }
                    return node
                })
            )
        }

        setShowModal(false)
    }, [
        selectedNodeForModal,
        nodeTitle,
        questionType,
        questionContent,
        optionsDict,
        followedNodes,
        dependsOnNodes,
        followedNodeKeys,
        autonomousInstruction,
        nodes,
        showFeedback,
    ])

    const addOption = useCallback(() => {
        setOptionsDict((prevOptions) => {
            const newKey = `Nova Opção ${Object.keys(prevOptions).length + 1}`
            return {
                ...prevOptions,
                [newKey]: '',
            }
        })
    }, [])

    const removeOption = useCallback((key: string) => {
        setOptionsDict((prevOptions) => {
            const newDict = {...prevOptions}
            delete newDict[key]
            return newDict
        })
    }, [])

    const handleOptionDragStart = useCallback((key: string) => {
        setDraggedOptionKey(key)
    }, [])

    const handleOptionDragOver = useCallback(
        (event: React.DragEvent<HTMLDivElement>, key: string) => {
            event.preventDefault()
            if (dragOverOptionKey !== key) {
                setDragOverOptionKey(key)
            }
        },
        [dragOverOptionKey]
    )

    const handleOptionDrop = useCallback(
        (targetKey: string) => {
            setOptionsDict((prevOptions) => {
                if (!draggedOptionKey || draggedOptionKey === targetKey) {
                    return prevOptions
                }

                const entries = Object.entries(prevOptions)
                const fromIndex = entries.findIndex(
                    ([key]) => key === draggedOptionKey
                )
                const toIndex = entries.findIndex(([key]) => key === targetKey)

                if (fromIndex === -1 || toIndex === -1) {
                    return prevOptions
                }

                const reordered = [...entries]
                const [movedEntry] = reordered.splice(fromIndex, 1)
                reordered.splice(toIndex, 0, movedEntry)

                return Object.fromEntries(reordered)
            })

            setDraggedOptionKey(null)
            setDragOverOptionKey(null)
        },
        [draggedOptionKey]
    )

    const handleOptionDragEnd = useCallback(() => {
        setDraggedOptionKey(null)
        setDragOverOptionKey(null)
    }, [])

    const updateOptionKey = useCallback((oldKey: string, newKey: string) => {
        if (oldKey === newKey) return

        setOptionsDict((prevOptions) => {
            if (!Object.prototype.hasOwnProperty.call(prevOptions, oldKey)) {
                return prevOptions
            }

            if (
                newKey !== oldKey &&
                Object.prototype.hasOwnProperty.call(prevOptions, newKey)
            ) {
                return prevOptions
            }

            const entries = Object.entries(prevOptions)
            const renamedEntries = entries.map(([key, value]) =>
                key === oldKey ? [newKey, value] : [key, value]
            )

            return Object.fromEntries(renamedEntries)
        })
    }, [])

    const updateOptionValue = useCallback((key: string, value: string) => {
        setOptionsDict((prevOptions) => ({
            ...prevOptions,
            [key]: value,
        }))
    }, [])

    const generateValueFromLabel = useCallback((label: string): string => {
        return label
            .toLowerCase()
            .trim()
            .replace(/\s+/g, '_')
            .replace(/[^a-z0-9_]/g, '')
    }, [])

    const addFollowedNode = useCallback((nodeId: string) => {
        if (!nodeId) return

        setFollowedNodes((previous) => [...previous, nodeId])
        setFollowedNodeKeys((previous) => [...previous, ''])
    }, [])

    const removeFollowedNode = useCallback((indexToRemove: number) => {
        setFollowedNodes((previous) =>
            previous.filter((_, index) => index !== indexToRemove)
        )

        setFollowedNodeKeys((previous) =>
            previous.filter((_, index) => index !== indexToRemove)
        )
    }, [])

    const updateFollowedNodeKey = useCallback((index: number, key: string) => {
        setFollowedNodeKeys((previous) => {
            const updated = [...previous]
            updated[index] = key
            return updated
        })
    }, [])

    const addDependsOnNode = useCallback((nodeId: string) => {
        if (!nodeId) return

        setDependsOnNodes((previous) => {
            if (previous.includes(nodeId)) return previous
            return [...previous, nodeId]
        })
    }, [])

    const removeDependsOnNode = useCallback((nodeId: string) => {
        setDependsOnNodes((previous) => previous.filter((id) => id !== nodeId))
    }, [])

    useEffect(() => {
        setFollowedNodeKeys((previous) => {
            const optionValues = isOptionBasedQuestionType(questionType)
                ? Object.values(optionsDict)
                      .map((value) => String(value).trim())
                      .filter((value) => value !== '')
                : []

            const next = followedNodes.map((_, index) => {
                const currentValue = previous[index] || ''
                if (currentValue.trim() !== '') {
                    return currentValue
                }

                const fallbackValue = isOptionBasedQuestionType(questionType)
                    ? optionValues[index] || ''
                    : getDefaultFollowingQuestionKey(index)

                return fallbackValue
            })

            const changed =
                next.length !== previous.length ||
                next.some((value, index) => value !== previous[index])

            return changed ? next : previous
        })
    }, [followedNodes, questionType, optionsDict])

    const moveFollowedNodeUp = useCallback((index: number) => {
        setFollowedNodes((previous) => {
            if (index <= 0 || index >= previous.length) {
                return previous
            }

            const reordered = [...previous]
            ;[reordered[index - 1], reordered[index]] = [
                reordered[index],
                reordered[index - 1],
            ]

            return reordered
        })

        setFollowedNodeKeys((previous) => {
            if (index <= 0 || index >= previous.length) {
                return previous
            }

            const reordered = [...previous]
            ;[reordered[index - 1], reordered[index]] = [
                reordered[index],
                reordered[index - 1],
            ]

            return reordered
        })
    }, [])

    const moveFollowedNodeDown = useCallback((index: number) => {
        setFollowedNodes((previous) => {
            if (index < 0 || index >= previous.length - 1) {
                return previous
            }

            const reordered = [...previous]
            ;[reordered[index], reordered[index + 1]] = [
                reordered[index + 1],
                reordered[index],
            ]

            return reordered
        })

        setFollowedNodeKeys((previous) => {
            if (index < 0 || index >= previous.length - 1) {
                return previous
            }

            const reordered = [...previous]
            ;[reordered[index], reordered[index + 1]] = [
                reordered[index + 1],
                reordered[index],
            ]

            return reordered
        })
    }, [])

    const setFollowedNodeAsDefault = useCallback((index: number) => {
        setFollowedNodes((previous) => {
            if (index <= 0 || index >= previous.length) {
                return previous
            }

            const reordered = [...previous]
            const [targetId] = reordered.splice(index, 1)
            reordered.unshift(targetId)

            return reordered
        })

        setFollowedNodeKeys((previous) => {
            if (index <= 0 || index >= previous.length) {
                return previous
            }

            const reordered = [...previous]
            const [targetKey] = reordered.splice(index, 1)
            reordered.unshift(targetKey)

            return reordered
        })
    }, [])

    const modalFLowSelect = () => {
        const normalizedFilter = flowFilter.trim().toLowerCase()
        const filteredFlows = flows.filter((item) =>
            item.name.toLowerCase().includes(normalizedFilter)
        )

        return (
            <div
                style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: 'rgba(15, 23, 42, 0.4)',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    zIndex: 2000,
                }}
                onClick={() => setShowModalFlow(false)}
            >
                <div
                    style={{
                        background: 'white',
                        padding: '24px',
                        width: '320px',
                        borderRadius: '8px',
                        boxShadow: '0 4px 12px rgba(15, 23, 42, 0.15)',
                    }}
                    onClick={(e) => e.stopPropagation()}
                >
                    <h2>Selecionar Fluxo</h2>
                    <div style={{marginTop: '12px'}}>
                        <input
                            type="text"
                            value={flowFilter}
                            onChange={(e) => setFlowFilter(e.target.value)}
                            placeholder="Filtrar fluxos..."
                            style={{
                                width: '100%',
                                padding: '8px 10px',
                                borderRadius: '6px',
                                border: '1px solid #d1d5db',
                                boxSizing: 'border-box',
                            }}
                        />
                    </div>
                    <div
                        style={{
                            marginTop: '12px',
                            display: 'flex',
                            flexDirection: 'column',
                            gap: '8px',
                            maxHeight: '220px',
                            overflowY: 'auto',
                        }}
                    >
                        {filteredFlows.length === 0 && (
                            <div
                                style={{
                                    padding: '12px',
                                    textAlign: 'center',
                                    color: '#6b7280',
                                    border: '1px dashed #d1d5db',
                                    borderRadius: '6px',
                                }}
                            >
                                Nenhum fluxo encontrado.
                            </div>
                        )}
                        {filteredFlows.map((item) => (
                            <button
                                key={item.id}
                                onClick={() => {
                                    setFlowData(item.id)
                                    setShowModalFlow(false)
                                }}
                                style={{
                                    textAlign: 'left',
                                    padding: '10px 12px',
                                    borderRadius: '6px',
                                    border: '1px solid #e5e7eb',
                                    backgroundColor: '#f9fafb',
                                    cursor: 'pointer',
                                    fontSize: '14px',
                                }}
                            >
                                {item.name}
                            </button>
                        ))}
                    </div>
                    <button
                        onClick={() => setShowModalFlow(false)}
                        style={{
                            marginTop: '20px',
                            padding: '10px 20px',
                            backgroundColor: '#007bff',
                            color: 'white',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: 'pointer',
                        }}
                    >
                        Fechar
                    </button>
                </div>
            </div>
        )
    }

    const handleCreateFlow = useCallback(async () => {
        const trimmedName = newFlowName.trim()
        if (!trimmedName || isCreatingFlow) {
            return
        }

        setIsCreatingFlow(true)

        try {
            await flowApi.createFlow({
                document_flow: {
                    id: uuidv4(),
                    name: trimmedName,
                },
            })

            const refreshedFlows = await flowApi.getFlow()
            setFlows(refreshedFlows || [])

            setShowCreateFlowModal(false)
            setNewFlowName('')
            showFeedback('Fluxo criado com sucesso.', 'success')
        } catch (error) {
            console.error('Erro ao criar novo fluxo:', error)
            showFeedback('Falha ao criar o fluxo.', 'error')
        } finally {
            setIsCreatingFlow(false)
        }
    }, [newFlowName, isCreatingFlow, flowApi, setFlows, showFeedback])

    const handleCloneFlow = useCallback(async () => {
        const trimmedName = cloneFlowName.trim()
        if (!trimmedName || !selectedFlowToCloneId || isCreatingFlow) {
            return
        }

        setIsCreatingFlow(true)

        try {
            const flowToClone = await flowApi.getFlowById(selectedFlowToCloneId)
            const {errors: _errors, ...flowToCloneWithoutErrors} = flowToClone

            await flowApi.createFlow({
                document_flow: {
                    ...flowToCloneWithoutErrors,
                    id: uuidv4(),
                    name: trimmedName,
                },
            })

            const refreshedFlows = await flowApi.getFlow()
            setFlows(refreshedFlows || [])

            setShowCloneFlowModal(false)
            setCloneFlowName('')
            setCloneFlowFilter('')
            setSelectedFlowToCloneId('')
            showFeedback('Fluxo clonado com sucesso.', 'success')
        } catch (error) {
            console.error('Erro ao clonar fluxo:', error)
            showFeedback('Falha ao clonar o fluxo.', 'error')
        } finally {
            setIsCreatingFlow(false)
        }
    }, [
        cloneFlowName,
        selectedFlowToCloneId,
        isCreatingFlow,
        flowApi,
        setFlows,
        showFeedback,
    ])

    const modalCreateFlow = () => {
        return (
            <div
                style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: 'rgba(15, 23, 42, 0.4)',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    zIndex: 2000,
                }}
                onClick={() => setShowCreateFlowModal(false)}
            >
                <div
                    style={{
                        background: 'white',
                        padding: '24px',
                        width: '360px',
                        borderRadius: '8px',
                        boxShadow: '0 4px 12px rgba(15, 23, 42, 0.15)',
                    }}
                    onClick={(e) => e.stopPropagation()}
                >
                    <h2>Criar novo Fluxo</h2>

                    <div style={{marginTop: '12px'}}>
                        <label
                            style={{
                                display: 'block',
                                marginBottom: '6px',
                                fontSize: '14px',
                            }}
                        >
                            Nome do fluxo
                        </label>
                        <input
                            type="text"
                            value={newFlowName}
                            onChange={(e) => setNewFlowName(e.target.value)}
                            placeholder="Digite o nome do fluxo"
                            style={{
                                width: '100%',
                                padding: '8px 10px',
                                borderRadius: '6px',
                                border: '1px solid #d1d5db',
                                boxSizing: 'border-box',
                            }}
                        />
                    </div>

                    <div
                        style={{
                            marginTop: '16px',
                            display: 'flex',
                            gap: '10px',
                            justifyContent: 'flex-end',
                        }}
                    >
                        <button
                            onClick={() => setShowCreateFlowModal(false)}
                            style={{
                                padding: '10px 16px',
                                backgroundColor: '#6c757d',
                                color: 'white',
                                border: 'none',
                                borderRadius: '4px',
                                cursor: 'pointer',
                            }}
                        >
                            Cancelar
                        </button>
                        <button
                            onClick={handleCreateFlow}
                            disabled={!newFlowName.trim() || isCreatingFlow}
                            style={{
                                padding: '10px 16px',
                                backgroundColor: '#007bff',
                                color: 'white',
                                border: 'none',
                                borderRadius: '4px',
                                cursor:
                                    !newFlowName.trim() || isCreatingFlow
                                        ? 'not-allowed'
                                        : 'pointer',
                                opacity:
                                    !newFlowName.trim() || isCreatingFlow
                                        ? 0.7
                                        : 1,
                            }}
                        >
                            {isCreatingFlow ? 'Criando...' : 'Criar'}
                        </button>
                    </div>
                </div>
            </div>
        )
    }

    const modalCloneFlow = () => {
        const normalizedFilter = cloneFlowFilter.trim().toLowerCase()
        const filteredFlows = flows.filter((item) =>
            item.name.toLowerCase().includes(normalizedFilter)
        )

        return (
            <div
                style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: 'rgba(15, 23, 42, 0.4)',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    zIndex: 2000,
                }}
                onClick={() => setShowCloneFlowModal(false)}
            >
                <div
                    style={{
                        background: 'white',
                        padding: '24px',
                        width: '360px',
                        borderRadius: '8px',
                        boxShadow: '0 4px 12px rgba(15, 23, 42, 0.15)',
                    }}
                    onClick={(e) => e.stopPropagation()}
                >
                    <h2>Clonar Fluxo</h2>

                    <div style={{marginTop: '12px'}}>
                        <label
                            style={{
                                display: 'block',
                                marginBottom: '6px',
                                fontSize: '14px',
                            }}
                        >
                            Nome do novo fluxo
                        </label>
                        <input
                            type="text"
                            value={cloneFlowName}
                            onChange={(e) => setCloneFlowName(e.target.value)}
                            placeholder="Digite o nome do novo fluxo"
                            style={{
                                width: '100%',
                                padding: '8px 10px',
                                borderRadius: '6px',
                                border: '1px solid #d1d5db',
                                boxSizing: 'border-box',
                            }}
                        />
                    </div>

                    <div style={{marginTop: '12px'}}>
                        <label
                            style={{
                                display: 'block',
                                marginBottom: '6px',
                                fontSize: '14px',
                            }}
                        >
                            Selecionar fluxo base
                        </label>
                        <input
                            type="text"
                            value={cloneFlowFilter}
                            onChange={(e) => setCloneFlowFilter(e.target.value)}
                            placeholder="Filtrar fluxos..."
                            style={{
                                width: '100%',
                                padding: '8px 10px',
                                borderRadius: '6px',
                                border: '1px solid #d1d5db',
                                boxSizing: 'border-box',
                            }}
                        />
                    </div>

                    <div
                        style={{
                            marginTop: '12px',
                            display: 'flex',
                            flexDirection: 'column',
                            gap: '8px',
                            maxHeight: '180px',
                            overflowY: 'auto',
                        }}
                    >
                        {filteredFlows.length === 0 && (
                            <div
                                style={{
                                    padding: '12px',
                                    textAlign: 'center',
                                    color: '#6b7280',
                                    border: '1px dashed #d1d5db',
                                    borderRadius: '6px',
                                }}
                            >
                                Nenhum fluxo encontrado.
                            </div>
                        )}
                        {filteredFlows.map((item) => {
                            const isSelected = selectedFlowToCloneId === item.id

                            return (
                                <button
                                    key={item.id}
                                    onClick={() =>
                                        setSelectedFlowToCloneId(item.id)
                                    }
                                    style={{
                                        textAlign: 'left',
                                        padding: '10px 12px',
                                        borderRadius: '6px',
                                        border: isSelected
                                            ? '1px solid #2563eb'
                                            : '1px solid #e5e7eb',
                                        backgroundColor: isSelected
                                            ? '#eff6ff'
                                            : '#f9fafb',
                                        cursor: 'pointer',
                                        fontSize: '14px',
                                    }}
                                >
                                    {item.name}
                                </button>
                            )
                        })}
                    </div>

                    <div
                        style={{
                            marginTop: '16px',
                            display: 'flex',
                            gap: '10px',
                            justifyContent: 'flex-end',
                        }}
                    >
                        <button
                            onClick={() => setShowCloneFlowModal(false)}
                            style={{
                                padding: '10px 16px',
                                backgroundColor: '#6c757d',
                                color: 'white',
                                border: 'none',
                                borderRadius: '4px',
                                cursor: 'pointer',
                            }}
                        >
                            Cancelar
                        </button>
                        <button
                            onClick={handleCloneFlow}
                            disabled={
                                !cloneFlowName.trim() ||
                                !selectedFlowToCloneId ||
                                isCreatingFlow
                            }
                            style={{
                                padding: '10px 16px',
                                backgroundColor: '#007bff',
                                color: 'white',
                                border: 'none',
                                borderRadius: '4px',
                                cursor:
                                    !cloneFlowName.trim() ||
                                    !selectedFlowToCloneId ||
                                    isCreatingFlow
                                        ? 'not-allowed'
                                        : 'pointer',
                                opacity:
                                    !cloneFlowName.trim() ||
                                    !selectedFlowToCloneId ||
                                    isCreatingFlow
                                        ? 0.7
                                        : 1,
                            }}
                        >
                            {isCreatingFlow ? 'Clonando...' : 'Clonar'}
                        </button>
                    </div>
                </div>
            </div>
        )
    }

    const modalQuestion = () => {
        const availableNodes = nodes.filter(
            (node) => node.id !== selectedNodeForModal?.id
        )
        const selectedQuestionId = selectedNodeForModal?.id || '-'
        const selectedQuestionIndex = selectedNodeForModal
            ? nodes.findIndex((node) => node.id === selectedNodeForModal.id)
            : -1

        const isOptionBasedType = [
            'select',
            'multiSelect',
            'table',
            'radio',
            'checkbox',
        ].includes(questionType)

        return (
            <div
                style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: 'rgba(15, 23, 42, 0.4)',
                    display: 'flex',
                    justifyContent: 'flex-end',
                    alignItems: 'stretch',
                    zIndex: 2000,
                }}
                onClick={() => setShowModal(false)}
            >
                <div
                    style={{
                        background: 'white',
                        padding: '24px',
                        width: '420px',
                        maxWidth: '480px',
                        height: '100%',
                        maxHeight: '100%',
                        overflowY: 'auto',
                        boxShadow: '-4px 0 24px rgba(15, 23, 42, 0.15)',
                        borderTopLeftRadius: '16px',
                        borderBottomLeftRadius: '16px',
                    }}
                    onClick={(e) => e.stopPropagation()}
                >
                    <h2>
                        {selectedNodeForModal?.id?.startsWith('n')
                            ? 'Nova Pergunta'
                            : 'Editar Pergunta'}
                    </h2>

                    <div
                        style={{
                            marginBottom: '15px',
                            padding: '10px 12px',
                            backgroundColor: '#f8f9fa',
                            border: '1px solid #e9ecef',
                            borderRadius: '6px',
                            display: 'flex',
                            flexDirection: 'column',
                            gap: '4px',
                            fontSize: '12px',
                            color: '#495057',
                        }}
                    >
                        <span>ID da pergunta: {selectedQuestionId}</span>
                        <span>
                            Índice no array:{' '}
                            {selectedQuestionIndex >= 0
                                ? selectedQuestionIndex
                                : '-'}
                        </span>
                    </div>

                    <div style={{marginBottom: '15px'}}>
                        <label style={{display: 'block', marginBottom: '5px'}}>
                            Label da Variável
                        </label>
                        <input
                            type="text"
                            value={nodeTitle}
                            onChange={(e) => setNodeTitle(e.target.value)}
                            style={{
                                width: '100%',
                                padding: '8px',
                                boxSizing: 'border-box',
                            }}
                        />
                    </div>

                    <div style={{marginBottom: '15px'}}>
                        <label style={{display: 'block', marginBottom: '5px'}}>
                            Tipo de Pergunta
                        </label>
                        <select
                            value={questionType}
                            onChange={(e) => {
                                const newType = e.target.value as any
                                setQuestionType(newType)

                                if (
                                    [
                                        'select',
                                        'multiSelect',
                                        'table',
                                        'radio',
                                        'checkbox',
                                    ].includes(newType)
                                ) {
                                    setOptionsDict({})
                                }

                                if (newType !== 'autonomous') {
                                    setAutonomousInstruction('')
                                }
                            }}
                            style={{width: '100%', padding: '8px'}}
                        >
                            <option value="text">Texto</option>
                            <option value="select">Select</option>
                            <option value="multiSelect">MultiSelect</option>
                            <option value="radio">Radio</option>
                            <option value="checkbox">Checkbox</option>
                            <option value="table">Tabela</option>
                            <option value="autonomous">Autônomo</option>
                            <option value="table_autonomous">
                                Tabela Autônoma
                            </option>
                        </select>
                    </div>

                    <div style={{marginBottom: '15px'}}>
                        <label style={{display: 'block', marginBottom: '5px'}}>
                            Título da Pergunta
                        </label>

                        <textarea
                            value={questionContent}
                            onChange={(e) => setQuestionContent(e.target.value)}
                            style={{
                                width: '100%',
                                padding: '8px',
                                minHeight: '80px',
                                boxSizing: 'border-box',
                            }}
                            placeholder={
                                questionType === 'table'
                                    ? 'Definir estrutura da tabela...'
                                    : 'Digite o título/conteúdo da pergunta...'
                            }
                        />

                        {isOptionBasedType && (
                            <div>
                                <div
                                    style={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                        alignItems: 'start',
                                        marginTop: '20px',
                                        marginBottom: '15px',
                                        padding: '10px',
                                        backgroundColor: '#f8f9fa',
                                        borderRadius: '6px',
                                        border: '1px solid #e9ecef',
                                    }}
                                >
                                    <div>
                                        <label
                                            style={{
                                                fontWeight: 'bold',
                                                color: '#333',
                                                display: 'block',
                                            }}
                                        >
                                            {questionType === 'table'
                                                ? 'Headers da Tabela'
                                                : 'Opções'}
                                        </label>
                                        <small
                                            style={{
                                                color: '#666',
                                                fontSize: '12px',
                                            }}
                                        >
                                            {questionType === 'table'
                                                ? 'Formato: "label" : "name" (enviado em type.table.headers)'
                                                : 'Formato: "Opção Exibida" : "valor_enviado"'}
                                        </small>
                                    </div>

                                    <button
                                        onClick={addOption}
                                        style={{
                                            padding: '8px 16px',
                                            width: '100%    ',
                                            marginTop: '12px',
                                            backgroundColor: 'transparent',
                                            color: '#007bff',
                                            border: '1px solid #007bff',
                                            borderRadius: '4px',
                                            fontSize: '14px',
                                            cursor: 'pointer',
                                        }}
                                    >
                                        {questionType === 'table'
                                            ? 'Adicionar Header'
                                            : 'Adicionar Opção'}
                                    </button>
                                </div>

                                {Object.keys(optionsDict).length === 0 && (
                                    <div
                                        style={{
                                            padding: '30px',
                                            textAlign: 'center',
                                            color: '#666',
                                            fontStyle: 'italic',
                                            border: '2px dashed #ddd',
                                            borderRadius: '8px',
                                            backgroundColor: '#fafafa',
                                        }}
                                    >
                                        <p
                                            style={{
                                                margin: '0 0 10px 0',
                                                fontSize: '16px',
                                            }}
                                        >
                                            {questionType === 'table'
                                                ? 'Nenhum header adicionado'
                                                : 'Nenhuma opção adicionada'}
                                        </p>
                                        <p
                                            style={{
                                                margin: '0',
                                                fontSize: '14px',
                                            }}
                                        >
                                            {questionType === 'table'
                                                ? 'Clique em "+ Adicionar Header" para começar'
                                                : 'Clique em "+ Adicionar Opção" para começar'}
                                        </p>
                                    </div>
                                )}

                                {Object.entries(optionsDict).map(
                                    ([key, value], index) => (
                                        <div
                                            key={`option-row-${index}`}
                                            onDragOver={(event) =>
                                                handleOptionDragOver(event, key)
                                            }
                                            onDrop={() => handleOptionDrop(key)}
                                            style={{
                                                flexDirection: 'column',
                                                gap: '10px',
                                                marginBottom: '12px',
                                                alignItems: 'flex-start',
                                                padding: '15px',
                                                backgroundColor:
                                                    dragOverOptionKey === key
                                                        ? '#e7f1ff'
                                                        : '#f8f9fa',
                                                borderRadius: '8px',
                                                border:
                                                    draggedOptionKey === key
                                                        ? '1px solid #0d6efd'
                                                        : '1px solid #e9ecef',
                                            }}
                                        >
                                            <div
                                                draggable
                                                onDragStart={() =>
                                                    handleOptionDragStart(key)
                                                }
                                                onDragEnd={handleOptionDragEnd}
                                                style={{
                                                    width: '100%',
                                                    display: 'flex',
                                                    justifyContent:
                                                        'space-between',
                                                    alignItems: 'center',
                                                    cursor: 'grab',
                                                    userSelect: 'none',
                                                }}
                                                title={
                                                    questionType === 'table'
                                                        ? 'Arraste para reordenar header'
                                                        : 'Arraste para reordenar opção'
                                                }
                                            >
                                                <small
                                                    style={{
                                                        color: '#6c757d',
                                                        fontWeight: 600,
                                                    }}
                                                >
                                                    Arraste para reordenar
                                                </small>
                                                <span
                                                    style={{
                                                        color: '#6c757d',
                                                        fontSize: '18px',
                                                        lineHeight: 1,
                                                    }}
                                                >
                                                    ::
                                                </span>
                                            </div>

                                            <div>
                                                <label
                                                    style={{
                                                        display: 'block',
                                                        fontSize: '14px',
                                                        fontWeight: '600',
                                                        marginBottom: '6px',
                                                        color: '#495057',
                                                    }}
                                                >
                                                    {questionType === 'table'
                                                        ? 'Label (enviado no payload):'
                                                        : 'Opção (exibida ao usuário):'}
                                                </label>
                                                <input
                                                    type="text"
                                                    value={key}
                                                    onChange={(e) => {
                                                        updateOptionKey(
                                                            key,
                                                            e.target.value
                                                        )

                                                        if (!value) {
                                                            const generatedValue =
                                                                generateValueFromLabel(
                                                                    e.target
                                                                        .value
                                                                )
                                                            updateOptionValue(
                                                                e.target.value,
                                                                generatedValue
                                                            )
                                                        }
                                                    }}
                                                    style={{
                                                        width: '100%',
                                                        padding: '8px',
                                                        border: '1px solid #ced4da',
                                                        borderRadius: '4px',
                                                        fontSize: '14px',
                                                    }}
                                                    placeholder={
                                                        questionType === 'table'
                                                            ? 'Ex: name'
                                                            : 'Ex: Primeira Opção'
                                                    }
                                                />
                                            </div>

                                            <div style={{flex: 1}}>
                                                <label
                                                    style={{
                                                        display: 'block',
                                                        fontSize: '14px',
                                                        fontWeight: '600',
                                                        marginBottom: '6px',
                                                        color: '#495057',
                                                    }}
                                                >
                                                    {questionType === 'table'
                                                        ? 'Nome (exibido para o usuário):'
                                                        : 'Valor (enviado no formulário):'}
                                                </label>
                                                <input
                                                    type="text"
                                                    value={value}
                                                    onChange={(e) =>
                                                        updateOptionValue(
                                                            key,
                                                            e.target.value
                                                        )
                                                    }
                                                    style={{
                                                        width: '100%',
                                                        padding: '8px',
                                                        border: '1px solid #ced4da',
                                                        borderRadius: '4px',
                                                        fontSize: '14px',
                                                    }}
                                                    placeholder={
                                                        questionType === 'table'
                                                            ? 'Ex: Nome do Item'
                                                            : 'Ex: primeira_opcao'
                                                    }
                                                />
                                            </div>

                                            <button
                                                onClick={() =>
                                                    removeOption(key)
                                                }
                                                style={{
                                                    width: '100%',
                                                    padding: '8px 12px',
                                                    backgroundColor: '#f8d7da',
                                                    color: '#dc3545',
                                                    border: '1px solid #dc3545',
                                                    borderRadius: '4px',
                                                    cursor: 'pointer',
                                                    fontSize: '12px',
                                                    marginTop: '24px',
                                                }}
                                                title="Remover opção"
                                            >
                                                {questionType === 'table'
                                                    ? 'Remover header'
                                                    : 'Remover opção'}
                                            </button>
                                        </div>
                                    )
                                )}
                            </div>
                        )}

                        {questionType === 'autonomous' && (
                            <div style={{marginTop: '15px'}}>
                                <label
                                    style={{
                                        display: 'block',
                                        marginBottom: '5px',
                                        fontWeight: 'bold',
                                    }}
                                >
                                    Instruções do modo autônomo
                                </label>
                                <textarea
                                    value={autonomousInstruction}
                                    onChange={(e) =>
                                        setAutonomousInstruction(e.target.value)
                                    }
                                    style={{
                                        width: '100%',
                                        padding: '8px',
                                        minHeight: '80px',
                                        boxSizing: 'border-box',
                                    }}
                                    placeholder="Descreva as instruções que serão usadas pelo assistente"
                                />
                            </div>
                        )}
                    </div>

                    {/* Próximos nós (múltiplos) */}
                    <div style={{marginBottom: '20px'}}>
                        <label
                            style={{
                                display: 'block',
                                marginBottom: '10px',
                                fontWeight: 'bold',
                            }}
                        >
                            Próximos Nós (Followed)
                        </label>

                        {/* Lista dos nós já selecionados */}
                        {followedNodes.length > 0 && (
                            <div style={{marginBottom: '10px'}}>
                                <label
                                    style={{
                                        display: 'block',
                                        marginBottom: '5px',
                                        fontSize: '12px',
                                        color: '#666',
                                    }}
                                >
                                    Nós conectados:
                                </label>
                                {followedNodes.map((nodeId, index) => {
                                    const node = nodes.find(
                                        (n) => n.id === nodeId
                                    )
                                    const priorityLabel =
                                        index === 0
                                            ? 'Default'
                                            : `Default ${index + 1}`
                                    const followedKey =
                                        followedNodeKeys[index] ||
                                        (isOptionBasedQuestionType(questionType)
                                            ? ''
                                            : priorityLabel)
                                    return (
                                        <div
                                            key={`${nodeId}-${index}`}
                                            style={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'space-between',
                                                backgroundColor: '#f8f9fa',
                                                padding: '8px 12px',
                                                borderRadius: '4px',
                                                marginBottom: '5px',
                                                border: '1px solid #e9ecef',
                                            }}
                                        >
                                            <div
                                                style={{
                                                    display: 'flex',
                                                    flexDirection: 'column',
                                                    gap: '4px',
                                                }}
                                            >
                                                <span
                                                    style={{
                                                        fontSize: '11px',
                                                        fontWeight: 700,
                                                        color: '#0f5132',
                                                    }}
                                                >
                                                    Prioridade: {priorityLabel}
                                                </span>
                                                <div
                                                    style={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        gap: '8px',
                                                    }}
                                                >
                                                    <span
                                                        style={{
                                                            fontSize: '12px',
                                                            color: '#495057',
                                                            minWidth: '52px',
                                                        }}
                                                    >
                                                        Chave:
                                                    </span>
                                                    <input
                                                        type="text"
                                                        value={followedKey}
                                                        onChange={(event) =>
                                                            updateFollowedNodeKey(
                                                                index,
                                                                event.target
                                                                    .value
                                                            )
                                                        }
                                                        placeholder={
                                                            isOptionBasedQuestionType(
                                                                questionType
                                                            )
                                                                ? 'Informe a chave da ligação'
                                                                : priorityLabel
                                                        }
                                                        style={{
                                                            width: '100%',
                                                            maxWidth: '220px',
                                                            padding: '4px 6px',
                                                            border: '1px solid #ced4da',
                                                            borderRadius: '4px',
                                                            fontSize: '12px',
                                                        }}
                                                    />
                                                </div>
                                                <span
                                                    style={{fontSize: '14px'}}
                                                >
                                                    {(node?.data
                                                        .rawLabel as string) ||
                                                        nodeId}{' '}
                                                    ({nodeId})
                                                </span>
                                            </div>
                                            <div
                                                style={{
                                                    display: 'flex',
                                                    gap: '6px',
                                                    alignItems: 'center',
                                                }}
                                            >
                                                <button
                                                    onClick={() =>
                                                        setFollowedNodeAsDefault(
                                                            index
                                                        )
                                                    }
                                                    disabled={index === 0}
                                                    style={{
                                                        padding: '4px 8px',
                                                        backgroundColor:
                                                            index === 0
                                                                ? '#6c757d'
                                                                : '#198754',
                                                        color: 'white',
                                                        border: 'none',
                                                        borderRadius: '3px',
                                                        fontSize: '11px',
                                                        cursor:
                                                            index === 0
                                                                ? 'not-allowed'
                                                                : 'pointer',
                                                    }}
                                                    title="Definir como Default"
                                                >
                                                    Default
                                                </button>
                                                <button
                                                    onClick={() =>
                                                        moveFollowedNodeUp(
                                                            index
                                                        )
                                                    }
                                                    disabled={index === 0}
                                                    style={{
                                                        padding: '4px 8px',
                                                        backgroundColor:
                                                            index === 0
                                                                ? '#adb5bd'
                                                                : '#0d6efd',
                                                        color: 'white',
                                                        border: 'none',
                                                        borderRadius: '3px',
                                                        fontSize: '12px',
                                                        cursor:
                                                            index === 0
                                                                ? 'not-allowed'
                                                                : 'pointer',
                                                    }}
                                                    title="Subir prioridade"
                                                >
                                                    ↑
                                                </button>
                                                <button
                                                    onClick={() =>
                                                        moveFollowedNodeDown(
                                                            index
                                                        )
                                                    }
                                                    disabled={
                                                        index ===
                                                        followedNodes.length - 1
                                                    }
                                                    style={{
                                                        padding: '4px 8px',
                                                        backgroundColor:
                                                            index ===
                                                            followedNodes.length -
                                                                1
                                                                ? '#adb5bd'
                                                                : '#0d6efd',
                                                        color: 'white',
                                                        border: 'none',
                                                        borderRadius: '3px',
                                                        fontSize: '12px',
                                                        cursor:
                                                            index ===
                                                            followedNodes.length -
                                                                1
                                                                ? 'not-allowed'
                                                                : 'pointer',
                                                    }}
                                                    title="Descer prioridade"
                                                >
                                                    ↓
                                                </button>
                                                <button
                                                    onClick={() =>
                                                        removeFollowedNode(
                                                            index
                                                        )
                                                    }
                                                    style={{
                                                        padding: '4px 8px',
                                                        backgroundColor:
                                                            '#dc3545',
                                                        color: 'white',
                                                        border: 'none',
                                                        borderRadius: '3px',
                                                        fontSize: '12px',
                                                        cursor: 'pointer',
                                                    }}
                                                >
                                                    ✕
                                                </button>
                                            </div>
                                        </div>
                                    )
                                })}
                            </div>
                        )}

                        {/* Seletor para adicionar novos nós */}
                        <div style={{display: 'flex', gap: '10px'}}>
                            <select
                                style={{
                                    flex: 1,
                                    padding: '8px',
                                    border: '1px solid #ddd',
                                    borderRadius: '4px',
                                }}
                                onChange={(e) => {
                                    if (e.target.value) {
                                        addFollowedNode(e.target.value)
                                        e.target.value = ''
                                    }
                                }}
                                defaultValue=""
                            >
                                <option value="">
                                    Selecione um nó para conectar...
                                </option>
                                {availableNodes.map((node) => (
                                    <option key={node.id} value={node.id}>
                                        {node.data.rawLabel as string} (
                                        {node.id})
                                    </option>
                                ))}
                            </select>
                        </div>
                        <small style={{color: '#666', fontSize: '12px'}}>
                            A chave de cada ligação será usada no
                            following_questions. Em perguntas de múltipla
                            escolha, o recomendado é usar o valor da opção.
                        </small>
                    </div>

                    {questionType === 'autonomous' && (
                        <div style={{marginBottom: '20px'}}>
                            <label
                                style={{
                                    display: 'block',
                                    marginBottom: '10px',
                                    fontWeight: 'bold',
                                }}
                            >
                                Dependências (depends_on)
                            </label>

                            {dependsOnNodes.length > 0 && (
                                <div style={{marginBottom: '10px'}}>
                                    {dependsOnNodes.map((nodeId) => {
                                        const node = nodes.find(
                                            (candidate) =>
                                                candidate.id === nodeId
                                        )

                                        return (
                                            <div
                                                key={`depends-on-${nodeId}`}
                                                style={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent:
                                                        'space-between',
                                                    backgroundColor: '#f8f9fa',
                                                    padding: '8px 12px',
                                                    borderRadius: '4px',
                                                    marginBottom: '5px',
                                                    border: '1px solid #e9ecef',
                                                }}
                                            >
                                                <span
                                                    style={{fontSize: '14px'}}
                                                >
                                                    {(node?.data
                                                        .rawLabel as string) ||
                                                        nodeId}{' '}
                                                    ({nodeId})
                                                </span>
                                                <button
                                                    onClick={() =>
                                                        removeDependsOnNode(
                                                            nodeId
                                                        )
                                                    }
                                                    style={{
                                                        padding: '4px 8px',
                                                        backgroundColor:
                                                            '#dc3545',
                                                        color: 'white',
                                                        border: 'none',
                                                        borderRadius: '3px',
                                                        fontSize: '12px',
                                                        cursor: 'pointer',
                                                    }}
                                                >
                                                    ✕
                                                </button>
                                            </div>
                                        )
                                    })}
                                </div>
                            )}

                            <div style={{display: 'flex', gap: '10px'}}>
                                <select
                                    style={{
                                        flex: 1,
                                        padding: '8px',
                                        border: '1px solid #ddd',
                                        borderRadius: '4px',
                                    }}
                                    onChange={(event) => {
                                        if (event.target.value) {
                                            addDependsOnNode(event.target.value)
                                            event.target.value = ''
                                        }
                                    }}
                                    defaultValue=""
                                >
                                    <option value="">
                                        Selecione uma questão para
                                        dependência...
                                    </option>
                                    {availableNodes
                                        .filter(
                                            (node) =>
                                                !dependsOnNodes.includes(
                                                    node.id
                                                )
                                        )
                                        .map((node) => (
                                            <option
                                                key={`depends-select-${node.id}`}
                                                value={node.id}
                                            >
                                                {node.data.rawLabel as string} (
                                                {node.id})
                                            </option>
                                        ))}
                                </select>
                            </div>
                            <small style={{color: '#666', fontSize: '12px'}}>
                                Os IDs selecionados serão salvos em depends_on e
                                não criam ligação visual.
                            </small>
                        </div>
                    )}

                    {/* Botões */}
                    <div
                        style={{
                            display: 'flex',
                            gap: '10px',
                            justifyContent: 'flex-end',
                            position: 'fixed',
                            bottom: 10,
                            right: 10,
                            width: '100%',
                        }}
                    >
                        <button
                            onClick={handleSaveQuestion}
                            style={{
                                padding: '12px 24px',
                                backgroundColor: '#007bff',
                                color: 'white',
                                border: 'none',
                                borderRadius: '6px',
                                cursor: 'pointer',
                                fontSize: '14px',
                                fontWeight: '500',
                            }}
                        >
                            Salvar
                        </button>
                        <button
                            onClick={() => setShowModal(false)}
                            style={{
                                padding: '12px 24px',
                                backgroundColor: '#6c757d',
                                color: 'white',
                                border: 'none',
                                borderRadius: '6px',
                                cursor: 'pointer',
                                fontSize: '14px',
                                fontWeight: '500',
                            }}
                        >
                            Cancelar
                        </button>
                    </div>
                </div>
            </div>
        )
    }

    if (isLoading) {
        return (
            <div
                style={{
                    height: '100vh',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                }}
            >
                <div>Carregando flow...</div>
            </div>
        )
    }

    const outlinedBtn: React.CSSProperties = {
        padding: '7px 12px',
        backgroundColor: '#ffffff',
        color: '#1f2328',
        border: '1px solid #d0d7de',
        borderRadius: '6px',
        cursor: 'pointer',
        fontSize: '13px',
        fontWeight: '500',
        fontFamily: 'Roboto, sans-serif',
        whiteSpace: 'nowrap',
    }

    const secondaryBtn: React.CSSProperties = {
        ...outlinedBtn,
        backgroundColor: '#f6f8fa',
        color: '#0969da',
        borderColor: '#0969da',
    }

    const primaryBtn: React.CSSProperties = {
        ...outlinedBtn,
        backgroundColor: '#3ACDB4',
        color: '#ffffff',
        border: 'none',
        fontWeight: '600',
    }

    const divider: React.CSSProperties = {
        width: '1px',
        height: '20px',
        backgroundColor: '#d0d7de',
        margin: '0 2px',
        flexShrink: 0,
    }

    return (
        <div style={{height: '100%', width: '100%', position: 'relative'}}>
            {contextFlowData?.name && (
                <div
                    style={{
                        position: 'absolute',
                        top: 10,
                        left: 50,
                        zIndex: 1000,
                        backgroundColor: '#ffffff',
                        border: '1px solid #ddd',
                        borderRadius: '4px',
                        padding: '8px 12px',
                        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                        fontSize: '14px',
                        fontWeight: '600',
                        color: '#111111',
                    }}
                >
                    Fluxo selecionado: {contextFlowData.name}
                </div>
            )}
            <div
                style={{
                    position: 'absolute',
                    bottom: 16,
                    right: 16,
                    zIndex: 1000,
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    backgroundColor: '#ffffff',
                    border: '1px solid #d0d7de',
                    borderRadius: '8px',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                    padding: '8px 12px',
                    fontFamily: 'Roboto, sans-serif',
                }}
            >
                {/* Navegação */}
                <button
                    onClick={() =>
                        navigate('/upload-documents', {
                            state: {flow: contextFlowData},
                        })
                    }
                    style={outlinedBtn}
                >
                    Editar documentos
                </button>

                <div style={divider} />

                {/* Gerenciamento de fluxo */}
                <button onClick={() => setShowModalFlow(true)} style={outlinedBtn}>
                    Selecionar fluxo
                </button>
                <button onClick={() => setShowCreateFlowModal(true)} style={outlinedBtn}>
                    Novo fluxo
                </button>
                <button onClick={() => setShowCloneFlowModal(true)} style={outlinedBtn}>
                    Clonar
                </button>

                <div style={divider} />

                {/* Edição */}
                <button onClick={addNewNode} style={secondaryBtn}>
                    + Pergunta
                </button>
                <button onClick={handleDownloadFlowImage} style={outlinedBtn}>
                    Baixar imagem
                </button>

                {/* Ação principal */}
                <button onClick={handleUpdateFlow} style={primaryBtn}>
                    Salvar fluxo
                </button>
            </div>

            <div
                ref={flowWrapperRef}
                className="flow-question-canvas"
                style={{height: '100%', width: '100%'}}
            >
                <ReactFlow
                    nodes={nodes}
                    edges={edges}
                    onNodesChange={onNodesChange}
                    onEdgesChange={onEdgesChange}
                    onConnect={onConnect}
                    onNodeClick={onNodeClick}
                    fitView
                    deleteKeyCode={['Delete']}
                    defaultEdgeOptions={{
                        style: {stroke: 'black', strokeWidth: 1.5},
                        markerEnd: {
                            type: MarkerType.ArrowClosed,
                            width: 12,
                            height: 12,
                            color: 'black',
                        },
                    }}
                >
                    <Background bgColor="#2f99e04d" />
                    <Panel
                        className="flex gap-1 rounded-md bg-primary-foreground p-1 text-foreground"
                        position="top-right"
                    >
                        <NodeSearch />
                    </Panel>
                    <Controls />
                </ReactFlow>
            </div>
            {showModal && modalQuestion()}
            {showModalFlow && modalFLowSelect()}
            {showCreateFlowModal && modalCreateFlow()}
            {showCloneFlowModal && modalCloneFlow()}
            <Snackbar
                open={snackbarOpen}
                autoHideDuration={4000}
                onClose={handleCloseSnackbar}
                anchorOrigin={{vertical: 'bottom', horizontal: 'center'}}
            >
                <Alert
                    onClose={handleCloseSnackbar}
                    severity={snackbarSeverity}
                    variant="filled"
                    sx={{width: '100%'}}
                >
                    {snackbarMessage}
                </Alert>
            </Snackbar>
        </div>
    )
}
